import React, { createContext, useContext, useState, useEffect } from "react";

interface AlternateProductData {
  quantity: number;
  replacementProductSwitch: number;
  replacementProductButton: number;
  noReplacement: boolean;
  contactMe: boolean;
  serialNumber?: string;
  noReplacementReason?: string;
}

interface AlternateProductContextType {
  alternateProductData: AlternateProductData;
  setAlternateProductData: React.Dispatch<
    React.SetStateAction<AlternateProductData>
  >;
  clearAlternateProductData: () => void;
}

const STORAGE_KEY = "alternateProductData";

const AlternateProductContext = createContext<
  AlternateProductContextType | undefined
>(undefined);

// Provider Component
export const AlternateProductProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [alternateProductData, setAlternateProductData] =
    useState<AlternateProductData>(() => {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        return stored
          ? JSON.parse(stored)
          : {
              quantity: 0,
              replacementProductSwitch: 0,
              replacementProductButton: 0,
              noReplacement: false,
              contactMe: false,
              serialNumber: undefined,
            };
      } catch {
        return {
          quantity: 0,
          replacementProductSwitch: 0,
          replacementProductButton: 0,
          noReplacement: false,
          contactMe: false,
          serialNumber: undefined,
        };
      }
    });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(alternateProductData));
    } catch {
      // ignore write errors
    }
  }, [alternateProductData]);

  const clearAlternateProductData = () => {
    setAlternateProductData({
      quantity: 0,
      replacementProductSwitch: 0,
      replacementProductButton: 0,
      noReplacement: false,
      contactMe: false,
      serialNumber: undefined,
    });
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <AlternateProductContext.Provider
      value={{
        alternateProductData,
        setAlternateProductData,
        clearAlternateProductData,
      }}
    >
      {children}
    </AlternateProductContext.Provider>
  );
};

// Custom Hook
export const useAlternateProductContext = () => {
  const context = useContext(AlternateProductContext);
  if (!context) {
    throw new Error(
      "useAlternateProductContext must be used within AlternateProductProvider"
    );
  }
  return context;
};
